// MySQL JSON operator lattice.
// PRD: Ralph/JsonColumnSemantics/PRD.md § Slice C2 (mysql row).
//
// MySQL has the native `JSON` type; loose-JSON columns declared on `TEXT`/
// `LONGTEXT` get the same lattice. Path operators are `->` (returns JSON) and
// `->>` (returns unquoted text). Function family: `JSON_CONTAINS`,
// `JSON_OVERLAPS` (8.0.17+), `JSON_CONTAINS_PATH`, `JSON_VALID`, `MEMBER OF`
// (8.0.17+).

import type { Operator } from '../types';

const MYSQL_JSON: readonly Operator[] = Object.freeze([
  { token: '=', label: 'equals', snippetTemplate: "= JSON_OBJECT('${1:key}', '${2:value}')" },
  { token: '<>', label: 'not equals', snippetTemplate: "<> JSON_OBJECT('${1:key}', '${2:value}')" },
  { token: 'IS NULL', label: 'is null', snippetTemplate: 'IS NULL' },
  { token: 'IS NOT NULL', label: 'is not null', snippetTemplate: 'IS NOT NULL' },
  {
    token: '->',
    label: 'extract path (returns JSON)',
    snippetTemplate: "-> '${1:$.path}'",
    docsUrl: 'https://dev.mysql.com/doc/refman/8.0/en/json-search-functions.html',
    requiresPath: true,
  },
  {
    token: '->>',
    label: 'extract path (returns text)',
    snippetTemplate: "->> '${1:$.path}'",
    docsUrl: 'https://dev.mysql.com/doc/refman/8.0/en/json-search-functions.html',
    requiresPath: true,
  },
  {
    token: 'JSON_CONTAINS',
    label: 'contains value at path',
    snippetTemplate: "JSON_CONTAINS(${1:col}, '${2:\"value\"}', '${3:$.path}')",
    docsUrl: 'https://dev.mysql.com/doc/refman/8.0/en/json-search-functions.html#function_json-contains',
    requiresPath: true,
  },
  {
    token: 'JSON_OVERLAPS',
    label: 'overlaps with value',
    snippetTemplate: "JSON_OVERLAPS(${1:col}, '${2:[1,2,3]}')",
    docsUrl: 'https://dev.mysql.com/doc/refman/8.0/en/json-search-functions.html#function_json-overlaps',
  },
  {
    token: 'MEMBER OF',
    label: 'value is member of JSON array',
    snippetTemplate: "'${1:value}' MEMBER OF (${2:col})",
    docsUrl: 'https://dev.mysql.com/doc/refman/8.0/en/json-search-functions.html#operator_member-of',
  },
  {
    token: 'JSON_CONTAINS_PATH',
    label: 'path exists',
    snippetTemplate: "JSON_CONTAINS_PATH(${1:col}, '${2|one,all|}', '${3:$.path}')",
    docsUrl: 'https://dev.mysql.com/doc/refman/8.0/en/json-search-functions.html#function_json-contains-path',
    requiresPath: true,
  },
  {
    token: 'JSON_VALID',
    label: 'is well-formed JSON',
    snippetTemplate: 'JSON_VALID(${1:col})',
    docsUrl: 'https://dev.mysql.com/doc/refman/8.0/en/json-attribute-functions.html#function_json-valid',
  },
]);

export function mysqlJsonOperatorsFor(): readonly Operator[] {
  return MYSQL_JSON;
}
